import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import {
  ShoppingCart,
  Payment,
  LocalShipping,
  CheckCircle,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  CalendarToday
} from '@mui/icons-material';
import MainService from '../services/MainService';

const steps = ['Order Review', 'Delivery Details', 'Confirmation'];

const Checkout = ({ cart, clearCart, user }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [deliveryDetails, setDeliveryDetails] = useState({
    address: user?.address || '',
    city: '',
    state: '',
    zipCode: '',
    phone: user?.phone || '',
    deliveryInstructions: '',
    deliveryDate: '',
    remarks: ''
  });
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });
  const [orderPlaced, setOrderPlaced] = useState(false);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const customerId = userData.CustomerId;

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (parseFloat(item.OfferPrice ) * (item.quantity )), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal();
  };

  // Load customer addresses
  const loadAddresses = async () => {
    if (!customerId) {
      setAddressError('Customer ID not found. Please login again.');
      return;
    }

    setAddressLoading(true);
    setAddressError('');
    try {
      const response = await MainService.getCustomerAddresses(customerId);
      if (response && Array.isArray(response)) {
        setAddresses(response);
      } else {
        setAddresses([]);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      setAddressError('Failed to load addresses. Please try again.');
    } finally {
      setAddressLoading(false);
    }
  };

  // Load addresses on component mount
  useEffect(() => {
    loadAddresses();
  }, []);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleDeliveryChange = (field) => (event) => {
    setDeliveryDetails({
      ...deliveryDetails,
      [field]: event.target.value
    });
  };

  const handlePaymentChange = (field) => (event) => {
    setPaymentDetails({
      ...paymentDetails,
      [field]: event.target.value
    });
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      setAddressError('Please select a delivery address');
      return;
    }

    if (!deliveryDetails.deliveryDate) {
      setAddressError('Please select a delivery date');
      return;
    }

    setLoading(true);
    setAddressError('');

    try {
      const bookingData = {
        DeliveryDate: deliveryDetails.deliveryDate,
        CustomerId: customerId,
        TotalDeliveryCharge: 0,
        GrandTotal: calculateTotal(),
        SubTotal: calculateSubtotal(),
        Remarks: deliveryDetails.remarks,
        FinalPrice: calculateTotal(),
        MobileNo: userData.MobileNo || userData.mobileNo || userData.UserName || '',
        PaymentMode: "Cash",
        CustomerAddressId: selectedAddress.Id || selectedAddress.id,
        BookingDetails: cart.map(item => ({
          Price: item.Price || item.price || 0,
          OfferPrice: item.OfferPrice || item.offerPrice || item.Price || item.price || 0,
          Quantity: item.quantity || 1,
          Total: (item.OfferPrice || item.offerPrice || item.Price || item.price || 0) * (item.quantity || 1),
          ProductId: item.Id || item.id
        }))
      };

      const response = await MainService.SaveBooking(bookingData);
      
      if (response === true || response === 1) {
        setOrderPlaced(true);
        clearCart();
        setActiveStep(2);
      } else {
        setAddressError('Failed to place order. Please try again.');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      setAddressError('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'home':
        return <HomeIcon />;
      case 'business':
        return <BusinessIcon />;
      default:
        return <LocationIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'home':
        return '#4CAF50';
      case 'business':
        return '#2196F3';
      default:
        return '#FF9800';
    }
  };

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
    setDeliveryDetails({
      ...deliveryDetails,
      address: address.Address,
      city: address.City,
      state: address.City, // Using city as state for now
      zipCode: address.POBox || '',
      phone: userData.MobileNo || userData.mobileNo || userData.UserName || ''
    });
  };

  const renderOrderReview = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Order Summary
      </Typography>
      {cart.map((item) => (
        <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>
            {item.Name} - {item.OfferPrice} x {item.quantity}
          </Typography>
          <Typography>₹{(item.OfferPrice * item.quantity).toFixed(2)}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Subtotal</Typography>
        <Typography>₹{calculateSubtotal().toFixed(2)}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Delivery</Typography>
        <Typography color="success.main">Free</Typography>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Total
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
          ₹{calculateTotal().toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );

  const renderDeliveryDetails = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
        Select Delivery Address
      </Typography>

      {addressError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {addressError}
        </Alert>
      )}

      {addressLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : addresses.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No addresses found. Please add an address from your profile.
        </Alert>
      ) : (
        <RadioGroup
          value={selectedAddress ? JSON.stringify(selectedAddress) : ''}
          onChange={(e) => {
            const address = JSON.parse(e.target.value);
            handleAddressSelect(address);
          }}
        >
          <Grid container spacing={2}>
            {addresses.map((address, index) => (
              <Grid item xs={12} key={index}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: selectedAddress && selectedAddress.Location === address.Location ? '2px solid #2E7D32' : '1px solid #e0e0e0',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#2E7D32',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => handleAddressSelect(address)}
                >
                  <CardContent>
                    <FormControlLabel
                      value={JSON.stringify(address)}
                      control={<Radio />}
                      label=""
                      sx={{ width: '100%', m: 0 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', ml: 3 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {getTypeIcon(address.Type)}
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {address.Location}
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {address.Address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {address.City}
                        </Typography>
                        {address.POBox && (
                          <Typography variant="body2" color="text.secondary">
                            PO Box: {address.POBox}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={address.Type}
                        size="small"
                        sx={{
                          backgroundColor: getTypeColor(address.Type),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
                 </RadioGroup>
       )}

       <Divider sx={{ my: 4 }} />

       <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
         Delivery Information
       </Typography>

       <Grid container spacing={3}>
         <Grid item xs={12} sm={6}>
           <TextField
             fullWidth
             label="Delivery Date"
             type="date"
             value={deliveryDetails.deliveryDate}
             onChange={handleDeliveryChange('deliveryDate')}
             InputLabelProps={{
               shrink: true,
             }}
             inputProps={{
               min: new Date().toISOString().split('T')[0], // Today as minimum date
             }}
             required
           />
         </Grid>
         <Grid item xs={12} sm={6}>
           <TextField
             fullWidth
             label="Phone Number"
             value={deliveryDetails.phone}
             onChange={handleDeliveryChange('phone')}
             placeholder="Delivery contact number"
           />
         </Grid>
         <Grid item xs={12}>
           <TextField
             fullWidth
             label="Remarks (Optional)"
             value={deliveryDetails.remarks}
             onChange={handleDeliveryChange('remarks')}
             multiline
             rows={3}
             placeholder="Any special instructions or notes for your order..."
           />
         </Grid>
       </Grid>
     </Box>
   );

  const renderPaymentDetails = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Card Number"
          value={paymentDetails.cardNumber}
          onChange={handlePaymentChange('cardNumber')}
          placeholder="1234 5678 9012 3456"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Cardholder Name"
          value={paymentDetails.cardName}
          onChange={handlePaymentChange('cardName')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Expiry Date"
          value={paymentDetails.expiryDate}
          onChange={handlePaymentChange('expiryDate')}
          placeholder="MM/YY"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="CVV"
          value={paymentDetails.cvv}
          onChange={handlePaymentChange('cvv')}
          placeholder="123"
        />
      </Grid>
    </Grid>
  );

  const renderConfirmation = () => (
    <Box sx={{ textAlign: 'center' }}>
      <CheckCircle sx={{ fontSize: 80, color: '#2E7D32', mb: 2 }} />
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
        Order Placed Successfully!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Your order has been confirmed and will be delivered within 2 hours.
      </Typography>
      <Button
        variant="contained"
        size="large"
        onClick={() => navigate('/')}
        sx={{
          backgroundColor: '#2E7D32',
          px: 4,
          py: 1.5,
          fontWeight: 'bold',
          '&:hover': {
            backgroundColor: '#1B5E20',
          },
        }}
      >
        Continue Shopping
      </Button>
    </Box>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderOrderReview();
      case 1:
        return renderDeliveryDetails();
      case 2:
        return renderConfirmation();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, px: 2 }}>
      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
        Checkout
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        Complete your order
      </Typography>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Card sx={{ width: '100%', minHeight: '400px' }}>
        <CardContent sx={{ p: 4, height: '100%' }}>
          {getStepContent(activeStep)}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      {activeStep < 2 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          {activeStep === 1 ? (
            <Button
              variant="contained"
              size="large"
              onClick={handlePlaceOrder}
              disabled={orderPlaced || loading}
              sx={{
                backgroundColor: '#2E7D32',
                py: 1.5,
                px: 4,
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#1B5E20',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : orderPlaced ? (
                'Processing...'
              ) : (
                'Place Order'
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                backgroundColor: '#2E7D32',
                '&:hover': {
                  backgroundColor: '#1B5E20',
                },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      )}
    </Container>
  );
};

export default Checkout; 